import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Truck, Zap, Scale, MapPin, Sparkles, Instagram, Facebook, Music2, Store } from "lucide-react";
import Marquee from "@/components/Marquee";
import Testimonials from "@/components/Testimonials";
import Reveal from "@/components/Reveal";
import FoilCard from "@/components/FoilCard";
import Mascot from "@/components/Mascot";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const heroLines = ["Every Collection", "Hides a Treasure.", "We Unlock Yours."];

const trustProps = [
  {
    Icon: Scale,
    title: "Fair Market Pricing",
    body: "Offers anchored to real market data — TCGplayer market price, recent comps, and condition — explained line by line, never lowballed in the dark.",
  },
  {
    Icon: Truck,
    title: "Insured Nationwide Shipping",
    body: "From Austin to anywhere in all 50 states: prepaid, fully insured labels and packing guidance so your cards travel like treasure.",
  },
  {
    Icon: Zap,
    title: "24-Hour Vault Payout",
    body: "Accept the offer and get paid fast — typically within 24 hours of verification — via the payout method that suits you best.",
  },
];

const categories = [
  { name: "Magic: The Gathering", tag: "ALPHA → MODERN", img: "https://images.unsplash.com/photo-1647893977168-6316f6c9ae44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxwb2tlbW9uJTIwbWFnaWMlMjB0cmFkaW5nJTIwY2FyZHMlMjBjb2xsZWN0aW9uJTIwZm9pbCUyMGdvbGQlMjB2YXVsdHxlbnwwfHx8fDE3ODg3MjgyNjR8MA&ixlib=rb-4.1.0&q=85", desc: "Reserved List staples, dual lands, Commander decks, and full collections." },
  { name: "Pokémon", tag: "BASE SET → ALT ARTS", img: "https://images.unsplash.com/photo-1672759043182-dde5467f8f76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwb2tlbW9uJTIwbWFnaWMlMjB0cmFkaW5nJTIwY2FyZHMlMjBjb2xsZWN0aW9uJTIwZm9pbCUyMGdvbGQlMjB2YXVsdHxlbnwwfHx8fDE3ODg3MjgyNjR8MA&ixlib=rb-4.1.0&q=85", desc: "Vintage holos, 1st editions, modern chase cards, and graded grails." },
  { name: "High-Value Singles", tag: "GRAILS & SLABS", img: "https://images.unsplash.com/photo-1672759043238-66899aeaf2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxwb2tlbW9uJTIwbWFnaWMlMjB0cmFkaW5nJTIwY2FyZHMlMjBjb2xsZWN0aW9uJTIwZm9pbCUyMGdvbGQlMjB2YXVsdHxlbnwwfHx8fDE3ODg3MjgyNjR8MA&ixlib=rb-4.1.0&q=85", desc: "Individual standouts — raw or graded — appraised card by card." },
  { name: "Sealed Products", tag: "BOXES & CASES", img: "https://images.unsplash.com/photo-1584429616537-1b9b495e7d6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwZ29sZCUyMHRyZWFzdXJlJTIwY2FyZHN8ZW58MHx8fHwxNzg4NzI4MjczfDA&ixlib=rb-4.1.0&q=85", desc: "Booster boxes, ETBs, and sealed cases, vintage to current print." },
];

const steps = [
  { n: "01", title: "Submit Your Details", body: "Tell us what you have — type, size, highlights — and attach photos or a card list. Two minutes, no obligation." },
  { n: "02", title: "Get a Collection Offer", body: "Ashton personally reviews every collection and replies within 24 hours with a transparent, market-based cash offer." },
  { n: "03", title: "Ship Free & Insured", body: "Accept and receive prepaid, fully insured shipping labels plus a packing guide. Your cards are covered door to door." },
  { n: "04", title: "Vault Payout", body: "Once verified, payment lands fast — usually within 24 hours. Treasure unlocked, quest complete." },
];

const chapters = [
  { n: "01", title: "Fair Market Pricing", body: "We publish how we price: live market data, honest condition grading, and offers you can verify against the same comps we use. A deal only counts when both sides see the map." },
  { n: "02", title: "Texas Integrity", body: "Born in Texas, built on handshakes. Every collection gets a named human — Ashton — not a ticket queue. Straight answers, no pressure, and your cards back safely if you pass." },
  { n: "03", title: "Collector Heritage", body: "We are collectors first. Your binder was built pack by pack, trade by trade. The Vault exists so the next chapter of that story starts with a payout worthy of the hunt." },
];

const faqs = [
  { q: "How is my collection valued?", a: "Every offer is anchored to live market data — TCGplayer market price, recent sold comps, and condition adjustments. We share the reasoning behind the number so you can check our math before you decide." },
  { q: "Do you buy bulk commons or only big hits?", a: "Both. We buy complete collections, bulk lots, high-value singles, and sealed product. For large bulk, we price by volume tiers; for singles and slabs, card by card." },
  { q: "I'm not in Texas. Can I still sell to the Vault?", a: "Absolutely — we buy from all 50 states. If your offer is accepted, we send prepaid, fully insured shipping labels and a packing guide. Distance never changes the deal." },
  { q: "How and when do I get paid?", a: "After your collection arrives and is verified, payment is typically sent within 24 hours via your preferred method. You'll get tracking and status updates at every step." },
  { q: "What if my cards are in mixed condition?", a: "No problem. Note the general condition in the form and add photos of the key cards. We grade conservatively and fairly — heavy play is priced as heavy play, mint as mint." },
  { q: "Is my information kept private?", a: "Yes. Your submission goes directly to Ashton and is used only to evaluate your collection and respond to you. We never sell or share seller information. See our Privacy Policy for details." },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  useEffect(() => {
    document.title = "Aardvarks Vault — Sell Your Trading Card Collection";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden px-5 pb-24 pt-40 sm:px-8 lg:pt-48" data-testid="home-hero">
        <div className="pointer-events-none absolute left-[8%] top-[18%] h-2 w-2 rotate-45 animate-spark bg-mint" aria-hidden="true" />
        <div className="pointer-events-none absolute right-[12%] top-[62%] h-1.5 w-1.5 rotate-45 animate-spark bg-gold [animation-delay:1.2s]" aria-hidden="true" />
        <div className="pointer-events-none absolute left-[45%] top-[10%] h-1.5 w-1.5 rotate-45 animate-spark bg-plum [animation-delay:2s]" aria-hidden="true" />

        <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.2fr_1fr]">
          <motion.div style={{ y: textY }}>
            <p className="eyebrow mb-6 flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-mint" aria-hidden="true" />
              Texas-Based · Buying Nationwide
            </p>
            <h1 className="font-serif text-4xl font-bold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
              {heroLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 1 ? "text-gold" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              className="mt-7 max-w-xl text-base leading-relaxed text-cream/80"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              Aardvarks Vault buys Magic: The Gathering, Pokémon, high-value singles, and sealed product from
              collectors in all 50 states — with honest market pricing, insured shipping, and payout in as little
              as 24 hours.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
            >
              <Link to="/sell" state={{ scrollToForm: true }} className="btn-gold" data-testid="hero-cta-primary">
                Sell to the Vault
              </Link>
              <Link to="/sell" className="btn-ghost" data-testid="hero-cta-secondary">
                Get a Collection Offer
              </Link>
            </motion.div>
            <motion.div
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.05 }}
            >
              {["All 50 States", "Insured Shipping", "24-Hr Payout"].map((b) => (
                <span key={b} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-cream/60">
                  <ShieldCheck size={14} className="text-mint" /> {b}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: cardY }}
            className="flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.9, rotate: 4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <FoilCard />
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* TRUST PROPS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-trust-section">
        <Reveal>
          <p className="eyebrow">Why Sellers Trust the Vault</p>
          <h2 className="mt-4 max-w-2xl font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            A handshake deal, backed by real market math
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {trustProps.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.12}>
              <div className="card-frame foil-sheen group h-full p-8" data-testid={`trust-card-${i}`}>
                <t.Icon size={30} className="text-gold transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-6 font-serif text-xl font-semibold text-gold">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/75">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-y border-gold/10 bg-ink/40 py-24" data-testid="home-categories-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">What We Hunt</p>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              Collections we buy, coast to coast
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.1}>
                <Link to="/sell" data-testid={`category-card-${i}`} className="card-frame foil-sheen group block overflow-hidden">
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={c.img}
                      alt={c.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-mint backdrop-blur">
                      {c.tag}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-lg font-semibold text-cream transition-colors group-hover:text-gold">{c.name}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-cream/65">{c.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-process-section">
        <Reveal>
          <p className="eyebrow">The Expedition Plan</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Selling is a four-step quest
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="relative h-full rounded-2xl border border-cream/10 bg-navy/60 p-7" data-testid={`process-step-${i}`}>
                <span className="font-display text-4xl font-black text-gold/25">{s.n}</span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-cream">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NATIONWIDE BANNER */}
      <section className="relative overflow-hidden border-y border-mint/15 bg-gradient-to-r from-ink via-navy to-ink py-20" data-testid="home-nationwide-banner">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-5 text-center sm:px-8">
          <Reveal>
            <img
              src="/brand/wordmark.png"
              alt="Aardvarks Vault"
              className="mx-auto w-60 sm:w-72"
              data-testid="nationwide-wordmark"
            />
            <MapPin className="mx-auto mt-6 text-mint" size={34} />
            <h2 className="mt-5 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              Rooted in Texas. <span className="text-mint">Buying in all 50 states.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-cream/75 sm:text-base">
              From Houston binders to Seattle shoeboxes — if the cards are good, the Vault comes to you. Prepaid
              insured labels, tracking at every step, and the same fair offer whether you're next door or three
              time zones away.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/sell" state={{ scrollToForm: true }} className="btn-gold" data-testid="nationwide-cta">
              Start Your Offer
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="mx-auto max-w-5xl px-5 py-28 sm:px-8" data-testid="home-manifesto-section">
        <Reveal className="text-center">
          <p className="eyebrow">The Vault Manifesto</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Three promises, carved in gold
          </h2>
        </Reveal>
        <div className="mt-16 space-y-14">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={0.05}>
              <div className="flex flex-col gap-5 border-l-2 border-gold/30 pl-8 sm:flex-row sm:items-start sm:gap-10" data-testid={`manifesto-chapter-${i}`}>
                <span className="font-display text-6xl font-black leading-none text-gold/30 sm:text-7xl">{c.n}</span>
                <div>
                  <h3 className="font-serif text-xl font-semibold text-gold sm:text-2xl">{c.title}</h3>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/75 sm:text-base">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MARKETPLACE & SOCIAL */}
      <section className="border-y border-plum/25 bg-ink/50 py-24" data-testid="home-destinations-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div className="flex items-center gap-4">
              <Mascot size={64} />
              <p className="eyebrow">Beyond the Vault Door</p>
            </div>
            <h2 className="mt-5 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              Follow the treasure drops
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-cream/75 sm:text-base">
              Curated singles, sealed breaks, and collection highlights surface first on our socials and in the
              Vault marketplace. Join the hunt — new finds appear weekly.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/marketplace" className="btn-ghost" data-testid="destinations-marketplace-btn">
                <Store size={15} /> Visit Marketplace
              </Link>
              <a href="https://instagram.com/AardvarksVault" target="_blank" rel="noopener noreferrer" className="btn-ghost !border-gold/50 !text-gold hover:!bg-gold/10" data-testid="destinations-instagram-btn">
                <Instagram size={15} /> @AardvarksVault
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-3 gap-4">
              {[
                { Icon: Instagram, label: "Instagram", handle: "@AardvarksVault", href: "https://instagram.com/AardvarksVault", id: "social-tile-instagram" },
                { Icon: Facebook, label: "Facebook", handle: "Aardvarks Vault", href: "https://facebook.com/AardvarksVault", id: "social-tile-facebook" },
                { Icon: Music2, label: "TikTok", handle: "@AardvarksVault", href: "https://tiktok.com/@AardvarksVault", id: "social-tile-tiktok" },
              ].map(({ Icon, label, handle, href, id }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={id}
                  className="card-frame foil-sheen group flex flex-col items-center gap-3 p-6 text-center transition-transform duration-300 hover:-translate-y-1"
                >
                  <Icon size={26} className="text-mint transition-colors group-hover:text-gold" />
                  <span className="text-sm font-semibold text-cream">{label}</span>
                  <span className="font-mono text-[10px] tracking-[0.15em] text-cream/55">{handle}</span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="home-testimonials-section">
        <Reveal className="text-center">
          <p className="eyebrow">Seller Stories</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Treasure hunters who struck gold
          </h2>
        </Reveal>
        <Testimonials />
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-24 sm:px-8" data-testid="home-faq-section">
        <Reveal className="text-center">
          <p className="eyebrow flex items-center justify-center gap-2">
            <Sparkles size={14} /> Vault Lore
          </p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Questions sellers ask us
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12" data-testid="home-faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-gold/15" data-testid={`faq-item-${i}`}>
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-serif text-base font-semibold text-cream hover:text-gold hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${i}`} className="text-sm leading-relaxed text-cream/70">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal delay={0.15} className="mt-14 text-center">
          <Link to="/sell" state={{ scrollToForm: true }} className="btn-gold" data-testid="faq-cta">
            Sell to the Vault
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
